import React from "react";
import {
  Package,
  DollarSign,
  Tag,
  FileText,
  Clock,
  Store,
  User,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  BarChart3,
  Hash
} from "lucide-react";
import { Modal, Badge } from "@/components/ui";

import { fmt } from "@/lib/formatters";

export const ItemDetailsModal = ({ item, inventory = [], onClose }) => {
  if (!item) return null;

  const isTransaction = item.items && Array.isArray(item.items);

  const resolveValue = (obj, fields = ['title', 'name', 'unitName', 'label', 'categoryName', 'vendorName', 'storeName', 'brandName']) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'object') {
      for (const f of fields) {
        if (obj[f] && typeof obj[f] === 'string') return obj[f];
      }
    }
    return null;
  };

  // Resolve nested properties
  const meta = item.item || {};
  const isMasterItem = !!(item.itemCode && item.itemName && !item.item);
  
  const name = isTransaction 
    ? (resolveValue(item.vendor) || resolveValue(item.supplier) || resolveValue(item.customer) || 'Transaction Record')
    : (meta.itemName || item.name || item.itemName || 'Unknown Item');
  
  const itemCode = isTransaction 
    ? (item.invoiceNumber || item.invoiceNo || 'N/A')
    : (meta.itemCode || item.itemCode || 'N/A');
  
  const sku = isTransaction
    ? (item.invoiceDate || item.date || item.createdAt ? new Date(item.invoiceDate || item.date || item.createdAt).toLocaleDateString() : 'N/A')
    : (meta.sku || item.sku || 'N/A');

  const brandDisplay = isTransaction ? 'N/A' : (resolveValue(item.brand) || resolveValue(meta.brand) || '—');
  const categoryDisplay = isTransaction ? 'Transaction' : (resolveValue(item.category) || resolveValue(meta.category) || '—');
  const storeDisplay = resolveValue(item.store) || resolveValue(meta.store) || 'Main Store';
  
  const unit = isTransaction ? 'ITEMS' : (resolveValue(meta.units) || resolveValue(meta.unit) || resolveValue(item.units) || resolveValue(item.unit) || 'NOS');
  const minQty = Number(meta.minQty || item.minQty || item.minStock || 0);
  const unitPrice = isTransaction ? 0 : Number(
    item.purchaseRate || item.unitPrice || item.rate || item.purchasePrice || item.price ||
    meta.purchaseRate || meta.unitPrice || meta.rate || meta.purchasePrice || meta.price ||
    (item.availableQty > 0 && item.totalCost ? item.totalCost / item.availableQty : 0) ||
    (item.availableQty > 0 && item.totalPurchaseAmount ? item.totalPurchaseAmount / item.availableQty : 0) ||
    0
  );

  // ── Purchase qty breakdown
  const originalQty = isTransaction 
    ? item.items.reduce((acc, it) => acc + Number(it.quantity || it.qty || 0), 0)
    : Number(item.purchasedQty ?? item.totalPurchased ?? item.purchased ?? item.originalQuantity ?? item.availableQty ?? item.stock ?? item.quantity ?? item.initialQty ?? 0);
  const returnedQty = Number(item.returnedQuantity || 0);
  const remainingQty = originalQty - returnedQty;
  const hasReturns = returnedQty > 0;

  // Master inventory items have availableQty/stock. Purchase records use remainingQty.
  let availableQty = 0;
  if (isTransaction) {
    availableQty = originalQty;
  } else {
    if (item.availableQty != null) {
      availableQty = Number(item.availableQty);
    } else if (item.stock != null) {
      availableQty = Number(item.stock);
    } else {
      // Look up in inventory prop
      const itemIdToFind = String(meta._id || item.itemId || item.item);
      const storeIdToFind = String(item.store?._id || item.store);
      
      const realInventoryItem = inventory?.find(i => 
        String(i.item?._id || i.item) === itemIdToFind && 
        String(i.store?._id || i.store) === storeIdToFind
      );
      
      if (realInventoryItem && realInventoryItem.stock != null) {
        availableQty = Number(realInventoryItem.stock);
      } else {
        availableQty = remainingQty;
      }
    }
  }

  // Determine status flag
  const statusKey = isTransaction ? 'in_stock' : (isMasterItem 
    ? (availableQty > minQty ? 'in_stock' : availableQty > 0 ? 'low_stock' : 'out_of_stock')
    : (availableQty > minQty ? 'in_stock' : availableQty > 0 ? 'low_stock' : 'out_of_stock'));

  // Financials
  const totalCost = Number(item.finalNetPayment || item.netBill || item.netPayment || item.totalAmount || item.totalCost || item.totalPurchaseAmount || (originalQty * unitPrice));
  const returnedValue = returnedQty * unitPrice;
  const remainingValue = remainingQty * unitPrice;
  const inventoryValue = isTransaction ? totalCost : (item.inventoryValue || item.totalPurchaseAmount || item.totalCost || (availableQty * unitPrice));
  const taxPercent = Number(item.taxPercent || item.taxP || item.gstRate || meta.taxPercent || meta.taxP || meta.gstRate || meta.gst || 0);

  const description = isTransaction
    ? `Transaction record with ${item.items.length} item(s).`
    : (meta.description || item.description || meta.itemDescription || item.itemDescription || meta.notes || item.notes || "No detailed description available for this item.");

  let totalGross = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  
  if (isTransaction) {
    (item.items || []).forEach(it => {
        const itQty = Number(it.quantity || it.qty || 0);
        const itRate = Number(it.purchaseRate || it.rate || it.unitPrice || it.price || 0);
        const discPer = Number(it.discountPercent || it.discountP || 0);
        const taxPer = Number(it.taxPercent || it.taxP || 0);
        
        const baseAmount = itQty * itRate;
        const discountAmt = (baseAmount * discPer) / 100;
        const taxable = baseAmount - discountAmt;
        const itTax = Number(it.taxAmount) || (taxable * taxPer / 100);
        
        totalGross += baseAmount;
        totalDiscount += discountAmt;
        totalTax += itTax;
    });
  }

  const paymentMode = isTransaction ? (item.paymentMode || item.payment?.paymentMode || 'N/A') : null;
  const billNo = isTransaction ? (item.billNo || item.invoiceNumber || item.invoiceNo || '—') : null;
  const otherCharges = isTransaction 
    ? (item.charges || []).reduce((acc, c) => acc + (Number(c.amount) || 0), 0) 
    : null;

  const hsnCode = isTransaction ? '—' : (meta.hsnCode || item.hsnCode || meta.hsnSacCode || item.hsnSacCode || '—');
  const barcode = isTransaction ? '—' : (meta.barcode || item.barcode || '—');

  const idLabel = isTransaction ? 'INV:' : 'ID:';
  const skuLabel = isTransaction ? 'DATE:' : 'SKU:';

  return (
    <Modal isOpen={!!item} onClose={onClose} title="Inventory Item Details (Full)" size="xl" className="max-w-4xl">
      <div className="space-y-6 overflow-y-auto pr-2 no-scrollbar pb-2">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pb-4 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-800 tracking-tight mb-2">{name}</h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-blue-50/50 border border-blue-100/50 px-2 py-0.5 rounded text-[10px] font-semibold text-brand-blue uppercase tracking-wider">
                <span className="text-blue-400">{idLabel}</span> {itemCode}
              </div>
              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                <span className="text-slate-400">{skuLabel}</span> {sku}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex flex-col items-end">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">{isTransaction ? 'TOTAL ITEMS' : 'TOTAL PURCHASED'}</span>
              <div className="flex items-baseline gap-1">
                <span className="text-lg font-bold text-brand-dark">{originalQty}</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase">{unit}</span>
              </div>
            </div>
            <div className="w-px h-8 bg-slate-100"></div>
            <div className="flex flex-col items-end">
              <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1">TOTAL COST</span>
              <span className="text-lg font-bold text-brand-dark">{totalCost > 0 ? fmt(totalCost) : '0'}</span>
            </div>
            <div className="w-px h-8 bg-slate-100"></div>
            <div className="flex flex-col items-end gap-1">
              <span className="text-[10px] font-bold text-amber-500 bg-amber-50 px-2 py-0.5 rounded border border-amber-100/50 uppercase">{item?.serviceType || item?.item?.serviceType || 'Generic'}</span>
              <span className="text-[9px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded uppercase">{categoryDisplay}</span>
            </div>
          </div>
        </div>

        {/* 3 Horizontal Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* STOCK & UNITS */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-blue-50 p-1.5 rounded-md">
                <Package size={14} className="text-brand-blue" />
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">STOCK & UNITS</span>
            </div>
            <div className="flex items-baseline gap-1.5 mb-6">
              <span className={`text-4xl font-black tracking-tighter ${statusKey === 'in_stock' ? 'text-emerald-500' : statusKey === 'low_stock' ? 'text-amber-500' : 'text-rose-500'}`}>{availableQty}</span>
              <span className="text-sm font-bold text-slate-400 uppercase">{unit}</span>
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-slate-100 border-dashed">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">MIN STOCK:</span>
              <span className="text-xs font-bold text-slate-700">{minQty} {unit}</span>
            </div>
          </div>

          {/* FINANCIALS */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-emerald-50 p-1.5 rounded-md">
                <DollarSign size={14} className="text-emerald-500" />
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">FINANCIALS</span>
            </div>
            <div className="mb-4">
              <span className="text-3xl font-black tracking-tighter text-slate-800">₹{fmt(isTransaction ? totalCost : unitPrice)}</span>
            </div>
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isTransaction ? 'TOTAL TAX:' : 'TAX CONFIG:'}</span>
                <span className="text-[10px] font-bold text-emerald-500">{isTransaction ? `₹${fmt(totalTax)}` : `${taxPercent}% GST`}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">PURCHASE TOTAL:</span>
                <span className="text-[11px] font-bold text-slate-700">{fmt(totalCost)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">INVENTORY VALUE:</span>
                <span className="text-[11px] font-bold text-brand-blue">{fmt(inventoryValue)}</span>
              </div>
            </div>
          </div>

          {/* SPECIFICATIONS */}
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="bg-indigo-50 p-1.5 rounded-md">
                <Tag size={14} className="text-indigo-500" />
              </div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isTransaction ? 'INVOICE SUMMARY' : 'SPECIFICATIONS'}</span>
            </div>
            <div className="space-y-4 mt-2">
              <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isTransaction ? 'GROSS AMOUNT:' : 'BRAND:'}</span>
                <span className="text-xs font-bold text-slate-700">{isTransaction ? `₹${fmt(totalGross)}` : brandDisplay}</span>
              </div>
              <div className="flex items-center justify-between border-b border-slate-50 pb-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isTransaction ? 'TOTAL DISCOUNT:' : 'HSN CODE:'}</span>
                <span className={`text-xs font-bold ${isTransaction && totalDiscount > 0 ? 'text-amber-500' : 'text-brand-blue'}`}>{isTransaction ? `₹${fmt(totalDiscount)}` : hsnCode}</span>
              </div>
              <div className={`flex items-center justify-between ${isTransaction ? 'border-b border-slate-50 pb-2' : ''}`}>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{isTransaction ? 'OTHER CHARGES:' : 'BARCODE:'}</span>
                <span className="text-xs font-medium text-slate-400">{isTransaction ? `₹${fmt(otherCharges)}` : barcode}</span>
              </div>
              {isTransaction && (
                <div className="flex flex-col gap-1 pt-1">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">NOTES:</span>
                  <span className="text-xs text-slate-600 leading-snug">{item.notes || item.description || "—"}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Item Description */}
        <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm mt-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="bg-slate-50 p-1 rounded">
              <FileText size={12} className="text-slate-400" />
            </div>
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">ITEM DESCRIPTION</span>
          </div>
          <p className="text-sm font-medium text-slate-600">
            {description}
          </p>
        </div>

        {/* Bottom Metadata Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 pt-4 px-2">
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">BV POINTS</span>
            <span className="text-xs font-bold text-slate-800">{item.bvPoints || meta.bvPoints || 0} PTS</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">STORE LOCATION</span>
            <span className="text-xs font-bold text-slate-800 uppercase">{storeDisplay}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">STATUS</span>
            <span className={`text-xs font-bold uppercase ${statusKey === 'in_stock' ? 'text-emerald-500' : statusKey === 'low_stock' ? 'text-amber-500' : 'text-rose-500'}`}>
              {statusKey === 'in_stock' ? 'IN STOCK' : statusKey === 'low_stock' ? 'LOW STOCK' : 'OUT OF STOCK'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">INVOICE NUMBER</span>
            <span className="text-xs font-bold text-brand-blue uppercase">{item.invoiceNumber || item.invoiceNo || 'OPEN-STOCK'}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">ENTRY / INVOICE DATE</span>
            <span className="text-xs font-bold text-brand-blue">
              {item.invoiceDate || item.date || item.createdAt || meta.createdAt ? new Date(item.invoiceDate || item.date || item.createdAt || meta.createdAt).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">CREATED ON</span>
            <span className="text-xs font-bold text-slate-800">
              {item.createdAt || meta.createdAt ? new Date(item.createdAt || meta.createdAt).toLocaleDateString() : '—'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">LAST UPDATED</span>
            <span className="text-xs font-bold text-slate-800">
              {item.updatedAt || meta.updatedAt || item.createdAt || meta.createdAt ? new Date(item.updatedAt || meta.updatedAt || item.createdAt || meta.createdAt).toLocaleDateString() : '—'}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">ITEM TYPE</span>
            <span className="text-xs font-medium text-slate-600 uppercase">{meta.itemType || item.itemType || meta.type || item.type || 'Product'}</span>
          </div>
        </div>

        {/* Items Table (For Transactions) */}
        {isTransaction && (
          <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden mt-4">
            <div className="flex items-center gap-2 p-4 border-b border-slate-100 bg-slate-50">
              <Package size={14} className="text-slate-500" />
              <span className="text-xs font-bold text-slate-600 uppercase tracking-widest">Items Included</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase font-bold tracking-wider">
                  <tr>
                    <th className="px-4 py-3 border-b border-slate-100">Item Name</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-left">Fixing Type</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Qty</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Rate</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Disc %</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Tax %</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Tax Amt</th>
                    <th className="px-4 py-3 border-b border-slate-100 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(item.items || []).map((it, idx) => {
                    const itName = it.itemName || it.item?.itemName || it.item?.name || 'Unknown Item';
                    const itFixingType = it.fixingType || it.item?.fixingType || '—';
                    const itQty = Number(it.quantity || it.qty || 0);
                    const itRate = Number(it.purchaseRate || it.rate || it.unitPrice || it.price || 0);
                    
                    const discPer = Number(it.discountPercent || it.discountP || 0);
                    const taxPer = Number(it.taxPercent || it.taxP || 0);
                    
                    const baseAmount = itQty * itRate;
                    const discountAmt = (baseAmount * discPer) / 100;
                    const taxable = baseAmount - discountAmt;
                    
                    const itTax = Number(it.taxAmount) || (taxable * taxPer / 100);
                    const itTotal = Number(it.totalAmount) || (taxable + itTax);
                    
                    return (
                      <tr key={idx} className="hover:bg-slate-50/50">
                        <td className="px-4 py-3 font-medium text-slate-700">{itName}</td>
                        <td className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500">{itFixingType}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-600">{itQty}</td>
                        <td className="px-4 py-3 text-right font-medium text-slate-600">₹{fmt(itRate)}</td>
                        <td className="px-4 py-3 text-right font-medium text-amber-600">{discPer}%</td>
                        <td className="px-4 py-3 text-right font-medium text-blue-600">{taxPer}%</td>
                        <td className="px-4 py-3 text-right font-medium text-emerald-600">₹{fmt(itTax)}</td>
                        <td className="px-4 py-3 text-right font-bold text-slate-800">₹{fmt(itTotal)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-end pt-6 border-t border-slate-100 mt-6">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors uppercase tracking-wider"
          >
            CLOSE DETAILS
          </button>
        </div>

      </div>
    </Modal>
  );
};

function getStatusVariant(key) {
  if (key === 'in_stock') return 'success';
  if (key === 'low_stock') return 'warning';
  return 'error';
}